import { useEffect } from 'react';

import { CheckoutShopToGiveEnrollmentSection } from '#components/molecules/checkout/CheckoutShopToGiveEnrollmentSection/CheckoutShopToGiveEnrollmentSection.js';
import { OrderSummary } from '#components/molecules/checkout/OrderSummary/OrderSummary.js';
import { getCartIdsFromCheckout, getCartItemCountFromCheckout } from '#pages/Checkout/utils.js';
import { useShowConsumerCheckout } from '@givebacks/api/store';
import { LoadingOverlay, Stack } from 'ui';

import { CheckoutOrderSection } from '../CheckoutOrderSection/CheckoutOrderSection';
import { useCheckoutProvider } from '../checkout-provider/useCheckoutProvider';
import classes from './CheckoutContent.module.css';

type CheckoutContentProps = {
  /** Called when the checkoutID returns no carts or errors */
  onCheckoutLoadingError: () => void;
  /** Additional props to pass to the container */
  containerProps?: React.ComponentProps<typeof Stack>;
  /** Whether to show the shop to give enrollment section
   * @default true
   */
  withEnrollmentSection?: boolean;
};

/**
 * Renders the checkout content for a given checkout id.
 * If the checkout returns no carts or errors the onCheckoutLoadingError function will be called.
 */
const CheckoutContent = ({
  onCheckoutLoadingError,
  containerProps,
  withEnrollmentSection = true,
}: CheckoutContentProps) => {
  const { checkoutId } = useCheckoutProvider();
  const { data: cartIds } = useShowConsumerCheckout({
    checkoutId: checkoutId,
    select: getCartIdsFromCheckout,
  });

  const {
    data: cartItemCount,
    isLoading,
    status,
    isLoadingError,
  } = useShowConsumerCheckout({
    checkoutId: checkoutId,
    select: getCartItemCountFromCheckout,
  });

  useEffect(() => {
    if (status !== 'pending') {
      if (isLoadingError) {
        onCheckoutLoadingError();
      }
      if (cartItemCount === 0) {
        onCheckoutLoadingError();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only need to run when the status changes
  }, [cartItemCount, isLoadingError, status]);

  if (isLoading) {
    return <LoadingOverlay visible={true} />;
  }

  // Redundant check for checkoutId, but it's here for ts compliance.
  if (cartItemCount === 0 || !cartIds) {
    return null;
  }

  return (
    <Stack
      className={classes.CheckoutContent_stack}
      {...containerProps}
      data-testid="checkout-content"
    >
      <OrderSummary checkoutId={checkoutId} />
      {withEnrollmentSection && <CheckoutShopToGiveEnrollmentSection checkoutId={checkoutId} />}
      <CheckoutOrderSection />
    </Stack>
  );
};

export { CheckoutContent };
